"use client";

import { useRef } from "react";
import { motion, useReducedMotion, useInView } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { Laptop, ShieldCheck, Globe } from "@/components/icons";
import { ACCENT_COLOR } from "./data";

const STEPS = [
  {
    num: "01",
    title: "Client request",
    body: "You configure your browser, scraper, or SDK to send its traffic to the proxy's IP and port instead of going to the target directly.",
  },
  {
    num: "02",
    title: "Proxy processing",
    body: "The proxy authenticates the client, applies any configured routing or access rules, and forwards the request from its own IP.",
  },
  {
    num: "03",
    title: "Server response",
    body: "The target server processes the request and sends the response back to the proxy. For traffic configured to use the proxy, the destination normally sees the proxy's IP, not the client's.",
  },
  {
    num: "04",
    title: "Proxy response",
    body: "The proxy relays the response back to your client. To the target, every request and reply looks like it came from the proxy.",
  },
];

export default function HowItWorks() {
  const reduced = useReducedMotion() ?? false;

  return (
    <section
      aria-labelledby="how-it-works-heading"
      className="border-border py-section relative border-b"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <p className="text-muted-foreground mb-3 text-xs font-semibold">The flow</p>
          <h2
            id="how-it-works-heading"
            className="text-foreground mb-3 scroll-mt-40 text-3xl font-bold tracking-tight sm:text-4xl"
          >
            How proxies work
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            Three hops, milliseconds apart. Your client, the proxy, and the target.
          </p>
        </div>

        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55 }}
          className="relative mx-auto max-w-3xl"
        >
          <RoundtripDiagram reduced={reduced} />
        </motion.div>

        <ol className="mt-6 grid list-none grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step, i) => (
            <motion.li
              key={step.num}
              initial={reduced ? false : { opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.35, delay: i * 0.06 }}
              className="flex items-start gap-3"
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums ${
                  i < 2 ? "text-brand-green" : "text-brand-yellow light:text-blue-600"
                }`}
                style={{
                  background: `color-mix(in srgb, ${i < 2 ? "var(--brand-green)" : "var(--brand-yellow)"} 8%, transparent)`,
                  border: `1px solid color-mix(in srgb, ${i < 2 ? "var(--brand-green)" : "var(--brand-yellow)"} 20%, transparent)`,
                }}
              >
                {i + 1}
              </span>
              <div className="flex flex-col gap-1">
                <span className="text-foreground text-sm font-semibold">{step.title}</span>
                <span className="text-muted-foreground text-sm leading-relaxed">{step.body}</span>
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const REQUEST_TINT = "var(--brand-green)"; // forward — client → target
const RESPONSE_TINT = "var(--yellow-accent)"; // return — target → client
// Distinct delays (s) double as stable React keys.
const FORWARD_DELAYS = [0, 1.6];
const RETURN_DELAYS = [0.8, 2.4];

// Roundtrip flow in the homepage product-card idiom: site-color panel with a
// faded blueprint grid, ambient glow, a preserve-3d proxy slab that floats on a
// looping z-bob, and request/response packets routed through the node.
function RoundtripDiagram({ reduced }: { reduced: boolean }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { margin: "100px 0px" });
  const active = !reduced && inView;

  return (
    <div
      ref={rootRef}
      className="bg-background relative h-[240px] w-full overflow-hidden"
      aria-label="Flow showing a request traveling from the client through the proxy to the target and the response returning the same way"
    >
      {/* Blueprint dotted grid — faded toward the edges so it blends into the page */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(color-mix(in srgb, var(--foreground) 6%, transparent) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
          maskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, #000 35%, transparent 92%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, #000 35%, transparent 92%)",
        }}
      />

      {/* Ambient glow behind the node */}
      <div
        className="pointer-events-none absolute top-1/2 left-1/2 mt-[-90px] ml-[-130px] h-[180px] w-[260px] rounded-full opacity-60 blur-3xl"
        style={{ background: `radial-gradient(closest-side, ${ACCENT_COLOR}1A, transparent)` }}
      />

      {/* Wire */}
      <div
        className="absolute top-1/2 right-[72px] left-[72px] h-px -translate-y-1/2"
        style={{
          background: `linear-gradient(90deg, transparent, color-mix(in srgb, ${REQUEST_TINT} 27%, transparent), color-mix(in srgb, ${RESPONSE_TINT} 27%, transparent), transparent)`,
        }}
      />

      {/* Forward (green) + return (blue) packets — pass behind the slab and re-emerge */}
      <div className="absolute top-1/2 right-[72px] left-[72px] h-px -translate-y-1/2">
        {FORWARD_DELAYS.map((delay) => (
          <motion.span
            key={`fwd-${delay}`}
            className="absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ background: REQUEST_TINT, boxShadow: `0 0 8px ${REQUEST_TINT}` }}
            initial={{ left: "0%", opacity: 0 }}
            animate={active ? { left: ["0%", "100%"], opacity: [0, 1, 1, 1, 0] } : undefined}
            transition={{ duration: 2.8, delay, repeat: Infinity, ease: "linear" }}
          />
        ))}
        {RETURN_DELAYS.map((delay) => (
          <motion.span
            key={`back-${delay}`}
            className="absolute top-1/2 h-2 w-2 -translate-x-1/2 translate-y-[5px] rounded-full"
            style={{ background: RESPONSE_TINT, boxShadow: `0 0 8px ${RESPONSE_TINT}` }}
            initial={{ left: "100%", opacity: 0 }}
            animate={active ? { left: ["100%", "0%"], opacity: [0, 1, 1, 1, 0] } : undefined}
            transition={{ duration: 2.8, delay, repeat: Infinity, ease: "linear" }}
          />
        ))}
      </div>

      {/* Client + Target endpoints */}
      <Endpoint className="left-3" icon={<Laptop size={18} aria-hidden="true" />} label="Client" />
      <Endpoint className="right-3" icon={<Globe size={18} aria-hidden="true" />} label="Target" />

      {/* 3D stage — isometric proxy slab, centered above the wire */}
      <div
        className="absolute inset-0 z-10 flex items-center justify-center"
        style={{ perspective: 1100 }}
      >
        <div
          className="relative h-[128px] w-[128px]"
          style={{ transformStyle: "preserve-3d", transform: "rotateX(57deg) rotateZ(-38deg)" }}
        >
          {/* Ghost wireframe layers */}
          <div className="absolute inset-0" style={{ transformStyle: "preserve-3d" }}>
            <div
              className="border-foreground/20 absolute inset-0 rounded-[18px] border border-dashed"
              style={{ transform: "translateZ(-24px)" }}
            />
            <div
              className="border-foreground/10 absolute inset-0 rounded-[18px] border border-dashed"
              style={{ transform: "translateZ(-40px)" }}
            />
          </div>

          {/* Floating slab */}
          <motion.div
            className="absolute inset-0"
            style={{ transformStyle: "preserve-3d" }}
            initial={{ z: 0 }}
            animate={active ? { z: [0, 2.5, 0, -2.5, 0] } : undefined}
            transition={{ duration: 4.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <div
              className="absolute -inset-2 rounded-[24px] bg-black/50 blur-lg"
              style={{ transform: "translateZ(-14px)" }}
            />
            <div
              className="absolute inset-0 rounded-[18px] bg-[#1a1d21]"
              style={{ transform: "translateZ(-10px)" }}
            />
            <div
              className="absolute right-[16px] bottom-0 left-[16px] h-[10px]"
              style={{
                transformOrigin: "bottom",
                transform: "rotateX(90deg)",
                background: "linear-gradient(180deg, #3a3e44 0%, #1e2025 100%)",
              }}
            />
            <div
              className="absolute top-[16px] right-0 bottom-[16px] w-[10px]"
              style={{
                transformOrigin: "right",
                transform: "rotateY(-90deg)",
                background: "linear-gradient(90deg, #2a2e33 0%, #16181c 100%)",
              }}
            />

            {/* Top face */}
            <div className="absolute inset-0 overflow-hidden rounded-[18px] border border-white/20 bg-[#0A0B0D]">
              <div className="absolute inset-0" style={{ backgroundColor: `${ACCENT_COLOR}0D` }} />
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, rgba(247,247,248,0.06), transparent 45%)",
                }}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-2xl border"
                  style={{
                    color: ACCENT_COLOR,
                    borderColor: `${ACCENT_COLOR}40`,
                    background: `${ACCENT_COLOR}12`,
                  }}
                >
                  <ShieldCheck size={22} aria-hidden="true" />
                </span>
                <span className="font-mono text-[8px] tracking-[0.22em] text-white/70">PROXY</span>
                <div className="flex items-center gap-1">
                  <span className="h-[3px] w-3 rounded-full bg-white/15" />
                  <span className="h-[3px] w-3 rounded-full bg-white/15" />
                  <motion.span
                    className="h-[5px] w-[5px] rounded-full"
                    style={{ background: ACCENT_COLOR, boxShadow: `0 0 6px ${ACCENT_COLOR}` }}
                    initial={{ opacity: 0.4 }}
                    animate={active ? { opacity: [0.4, 1, 0.4] } : undefined}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                  />
                </div>
              </div>
            </div>

            {/* Breathing accent ring */}
            {!reduced && (
              <motion.div
                className="pointer-events-none absolute inset-0 rounded-[18px] border"
                style={{
                  borderColor: `${ACCENT_COLOR}59`,
                  boxShadow: `0 0 18px ${ACCENT_COLOR}2E, inset 0 0 14px ${ACCENT_COLOR}17`,
                }}
                initial={{ opacity: 0 }}
                animate={active ? { opacity: [0, 0.6, 0] } : undefined}
                transition={{ duration: 4.8, delay: 0.9, repeat: Infinity, ease: "easeInOut" }}
              />
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Endpoint({
  className,
  icon,
  label,
}: {
  className: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div
      className={`absolute top-[calc(50%+14px)] z-20 flex -translate-y-1/2 flex-col items-center gap-1.5 ${className}`}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/12 bg-[#16181c] text-white/75">
        {icon}
      </span>
      <span className="text-muted-foreground text-[10px] font-medium">{label}</span>
    </div>
  );
}
